import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Flame, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Home,
  Building,
  Factory,
  Settings,
  Wrench,
  Truck,
  Star,
  Users,
  Mail,
  Zap
} from "lucide-react";

// Import welding images
import welderFabrication from "@/assets/welder-fabrication.jpg";
import weldingSteelFabrication from "@/assets/welding-steel-fabrication.jpg";
import structuralWelding from "@/assets/structural-welding.jpg";
import mobileWeldingService from "@/assets/mobile-welding-service.jpg";
import customMetalFabrication from "@/assets/custom-metal-fabrication.jpg";

const WeldingServices = () => {
  const faqData = [
    {
      question: "Do you provide welding services in Toronto?",
      answer: "Yes. Blue Knight Contracting provides professional welding services throughout Toronto and the GTA, including structural welding, fabrication, and repair work for residential and commercial projects."
    },
    {
      question: "Are your welders certified in Ontario?",
      answer: "Absolutely. All our welders are certified and experienced in various welding processes including MIG, TIG, stick welding, and specialized techniques for structural and industrial applications."
    },
    {
      question: "Do you offer mobile welding services?",
      answer: "Yes. We provide mobile welding services across Ontario, bringing our equipment directly to your job site for on-location welding, repairs, and fabrication work."
    },
    {
      question: "Can you handle structural steel welding?",
      answer: "Yes. Our certified welders specialize in structural steel welding for construction projects, building frameworks, and industrial applications following Ontario building codes."
    },
    {
      question: "Do you provide custom metal fabrication?",
      answer: "Yes. We offer complete custom metal fabrication services including design, cutting, forming, welding, and finishing for architectural, industrial, and decorative metalwork."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Welding Services Ontario",
    description: "Professional welding and metal fabrication services across Ontario including structural welding, custom fabrication, and mobile welding for residential and commercial projects.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Welding Services",
    areaServed: "Ontario, Canada"
  };

  const weldingServices = [
    {
      title: "Structural Welding",
      description: "Certified structural steel welding for construction projects and building frameworks.",
      icon: Building,
      services: [
        "Steel beam welding",
        "Building framework construction",
        "Structural repairs",
        "Code-compliant welding",
        "Heavy steel fabrication",
        "Construction site welding"
      ]
    },
    {
      title: "Custom Fabrication",
      description: "Complete metal fabrication services for custom projects and architectural metalwork.",
      icon: Settings,
      services: [
        "Custom metal design",
        "Architectural metalwork",
        "Railings and staircases",
        "Decorative metalwork",
        "Industrial fabrication",
        "Prototype development"
      ]
    },
    {
      title: "Mobile Welding",
      description: "On-site welding services bringing professional equipment directly to your location.",
      icon: Truck,
      services: [
        "On-site welding repairs",
        "Emergency welding services",
        "Equipment repair",
        "Field welding services",
        "Mobile fabrication",
        "Construction site support"
      ]
    },
    {
      title: "Industrial Welding",
      description: "Heavy-duty welding for industrial equipment, machinery, and manufacturing facilities.",
      icon: Factory,
      services: [
        "Industrial equipment repair",
        "Machinery welding",
        "Pipeline welding",
        "Tank and vessel welding",
        "Production line support",
        "Maintenance welding"
      ]
    },
    {
      title: "Repair Services",
      description: "Professional welding repairs for damaged metal components and equipment.",
      icon: Wrench,
      services: [
        "Crack repair welding",
        "Equipment restoration",
        "Structural damage repair",
        "Automotive welding",
        "Agricultural equipment repair",
        "Emergency repair services"
      ]
    },
    {
      title: "Specialized Welding",
      description: "Advanced welding techniques for specialized applications and materials.",
      icon: Zap,
      services: [
        "Aluminum welding",
        "Stainless steel welding",
        "TIG welding services",
        "Underwater welding",
        "Precision welding",
        "Exotic metal welding"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="Welding & Metal Fabrication Contractor | Niagara • Hamilton • GTA"
        description="Professional metal fabrication and on-site welding across Niagara, Hamilton & the GTA. Structural, repair and custom metal work."
        keywords="welding contractor Niagara, Hamilton metal fabrication, GTA welding service, steel repair, custom metal, mobile welding, railings"
        canonicalUrl="https://blueknightcontracting.ca/services/welding"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Expert Welding Services in Ontario
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional welding and metal fabrication services across Ontario. From structural steel welding to custom fabrication and mobile welding services, our certified welders deliver precision work that meets the highest standards.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Link to="/services">View All Services</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={weldingSteelFabrication} 
                  alt="Professional welder working on steel fabrication in Ontario workshop"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive Welding Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our certified welders provide complete welding and fabrication services using advanced techniques and equipment for lasting, quality results.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {weldingServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Welding Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of welding and fabrication work across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={welderFabrication} 
                    alt="Ontario welder fabricating steel components on-site"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Steel Fabrication</h3>
                  <p className="text-muted-foreground">
                    Precision steel fabrication and welding services
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={structuralWelding} 
                    alt="Ontario welder performing structural steel welding on construction site"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Structural Welding</h3>
                  <p className="text-muted-foreground">
                    Certified structural steel welding for construction
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={mobileWeldingService} 
                    alt="Mobile welding service truck with welder working on equipment repair"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Mobile Welding</h3>
                  <p className="text-muted-foreground">
                    On-site welding services at your location
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={customMetalFabrication} 
                    alt="Custom metal fabrication workshop showing precision welding work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Custom Fabrication</h3>
                  <p className="text-muted-foreground">
                    Custom metalwork and architectural fabrication
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Our Welding Process
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional welding services following industry standards and safety protocols
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-2">Project Assessment</h3>
                <p className="text-muted-foreground">
                  Detailed evaluation of welding requirements and material specifications
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-2">Preparation</h3>
                <p className="text-muted-foreground">
                  Material preparation, cutting, and setup for optimal welding conditions
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-2">Expert Welding</h3>
                <p className="text-muted-foreground">
                  Precision welding using appropriate techniques and safety protocols
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality Inspection</h3>
                <p className="text-muted-foreground">
                  Thorough inspection and testing to ensure weld quality and integrity
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Welding?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Certified Welders</h3>
                <p className="text-muted-foreground">
                  Experienced welders certified in multiple welding processes
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium welding materials and advanced equipment
                </p>
              </Card>

              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Safety First</h3>
                <p className="text-muted-foreground">
                  Strict safety protocols and comprehensive insurance coverage
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Mobile welding services across Ontario province
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Welding Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides professional welding services throughout Ontario, including mobile welding at your location
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our welding services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need Professional Welding Services?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for expert welding and fabrication services across Ontario. Get your free consultation and welding quote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free Welding Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Truck className="h-5 w-5 mr-2" />
                Mobile Welding Service
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default WeldingServices;